// GuidedFlow — the presenter-style overlay shell that runs ANY guide (a steps[]
// array). The same fixed bottom-left panel, clickable agenda, Back/Next/Auto
// controls, live status line, and the run-once-per-step + autoplay state machine
// the original New-Project demo used — now reusable for every entity's guide.
//
// Props:
//   guide:    { key, title, makeSteps(engine, ctx) -> steps[] }
//   ctx:      arbitrary context handed to the guide (auth, navigate, entity ids…)
//   navigate: react-router navigate (optional; engine falls back to history)
//   onClose:  close handler
//
// Each step: { title, lines[], run(), body(), autoAdvanceReady() }.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, ChevronRight, ChevronLeft, X, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { useGuidedEngine } from "./useGuidedEngine";

export default function GuidedFlow({ guide, ctx = {}, navigate, onClose }) {
  const engine = useGuidedEngine({ navigate });
  const { status, running, setRunning, setStatus } = engine;

  const [step, setStep] = useState(0);
  const [autoplay, setAutoplay] = useState(false);

  // Build the steps once (guides may close over engine + ctx + local setters).
  const stepsApi = useMemo(() => ({ setStep, setAutoplay }), []);
  const STEPS = useMemo(
    () => guide.makeSteps(engine, { ...ctx, ...stepsApi }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guide]
  );
  const N = STEPS.length;
  const cur = STEPS[Math.min(step, N - 1)];

  const runStep = useCallback(async () => {
    if (running) return;
    setRunning(true);
    try { if (cur.run) await cur.run(); }
    catch (e) { setStatus(`⚠ ${(e?.message || e).toString().slice(0, 90)}`); }
    finally { setRunning(false); }
  }, [cur, running, setRunning, setStatus]);

  // Run a step's action exactly once whenever `step` changes.
  const lastRunStep = useRef(-1);
  useEffect(() => {
    if (lastRunStep.current === step) return;
    lastRunStep.current = step;
    const t = setTimeout(() => { runStep(); }, step === 0 ? 300 : 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Autoplay: advance after a step settles, unless the step says it isn't ready
  // (e.g. waiting for the user to type a name or pick a file).
  useEffect(() => {
    if (!autoplay || running) return;
    if (step >= N - 1) return;
    const ready = cur.autoAdvanceReady ? cur.autoAdvanceReady() : true;
    if (!ready) return;
    const t = setTimeout(() => setStep((s) => Math.min(N - 1, s + 1)), 1400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, running, autoplay, cur]);

  const go = (k) => { if (k >= 0 && k < N) { lastRunStep.current = -1; setStep(k); } };

  return (
    <div className="fixed left-5 bottom-5 z-[60] w-[340px] max-w-[calc(100vw-2.5rem)] rounded-[10px] bg-card-bg shadow-2xl border border-page-divider text-page-text overflow-hidden select-none">
      <div className="flex items-center justify-between px-4 py-2.5 bg-nav-active text-white">
        <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles size={15} /> {guide.title || "Cortex Guided"}</div>
        <button onClick={() => onClose?.()} className="p-1 rounded hover:bg-white/20" title="Close guide"><X size={16} /></button>
      </div>

      <div className="px-3 py-2 border-b border-page-divider max-h-[200px] overflow-y-auto">
        {STEPS.map((s, i) => {
          const label = s.title.replace(/^\s*\d+\s*·\s*/, "");
          const isCur = i === step;
          const isDoneRow = i < step;
          return (
            <button key={i} onClick={() => go(i)}
              className={`w-full flex items-center gap-2 text-left text-xs px-2 py-1 rounded ${isCur ? "bg-page-bg font-semibold" : "hover:bg-page-bg/60"}`}>
              <span className="shrink-0 w-4 text-center">
                {isDoneRow ? <CheckCircle2 size={13} className="text-green-600 inline" /> : isCur ? <ChevronRight size={13} className="inline" /> : <span className="text-page-muted">{i}</span>}
              </span>
              <span className={isCur ? "" : "text-page-muted"}>{label}</span>
            </button>
          );
        })}
      </div>

      <div className="px-4 py-3">
        <div className="text-sm font-semibold mb-1">{cur.title}</div>
        <div className="text-xs text-page-muted space-y-1">
          {(cur.lines || []).map((l, i) => <div key={i} dangerouslySetInnerHTML={{ __html: l }} />)}
        </div>
        {cur.body && cur.body()}
        <div className="mt-2 text-xs flex items-center gap-1.5 min-h-[18px]">
          {running && <Loader2 size={12} className="animate-spin shrink-0" />}
          <span className={status.startsWith("⚠") ? "text-amber-600" : status.startsWith("✓") ? "text-green-600" : "text-page-text"}>{status}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-page-divider">
        <button onClick={() => go(step - 1)} disabled={step === 0 || running} className="flex items-center gap-1 h-8 px-3 rounded text-xs hover:bg-page-bg disabled:opacity-40"><ChevronLeft size={14} /> Back</button>
        <button onClick={() => go(step + 1)} disabled={step >= N - 1 || running} className="flex items-center gap-1 h-8 px-3 rounded bg-nav-active text-white text-xs disabled:opacity-40">Next <ChevronRight size={14} /></button>
        <button onClick={() => setAutoplay((a) => !a)} className="flex items-center gap-1 h-8 px-3 rounded text-xs hover:bg-page-bg ml-auto">
          {autoplay ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Auto</>}
        </button>
      </div>
    </div>
  );
}
